import { useI18n } from '../../context/I18nContext';
import SingleCableVisualization from './SingleCableVisualization';
import MultipleCableVisualization from './MultipleCableVisualization';
import MultipolarCableVisualization from './MultipolarCableVisualization';
import SheathCableVisualization from './SheathCableVisualization';
import { analyzeAndGroupCables } from '../../utils/cableLogic';


// ==================== COMPONENTE VISUALIZZAZIONE IBRIDA ====================

const HybridCableVisualization = ({ cables, settings, siglatura, hoveredCable, setHoveredCable, isMobile, availableSheaths = [] }) => {
    const { t } = useI18n();
    const multipolarCables = cables.filter(cable => cable.poli && Array.isArray(cable.poli));
    const standardCables = cables.filter(cable => !(cable.poli && Array.isArray(cable.poli)));
    
    // ✅ STEP 1: PRIORITÀ CAVI MULTIPLI - Raggruppa TUTTI i cavi standard (con e senza guaina)
    const allSubGroups = analyzeAndGroupCables(standardCables, settings);
    
    // ✅ STEP 2: Separa multipli, singoli con guaina, e singoli senza guaina
    const multipleGroups = [];
    const singleCablesWithSheath = [];
    const singleCablesWithoutSheath = [];
    
    allSubGroups.forEach(group => {
        if (group.isSingle) {
            const cable = group.cables[0];
            if (cable.guaina) {
                singleCablesWithSheath.push(cable);
            } else {
                singleCablesWithoutSheath.push(cable);
            }
        } else {
            multipleGroups.push(group);
        }
    });
    
    // ✅ STEP 3: Raggruppa i cavi singoli con guaina per ID guaina
    const sheathGroups = {};
    singleCablesWithSheath.forEach(cable => {
        if (!sheathGroups[cable.guaina]) {
            sheathGroups[cable.guaina] = [];
        }
        sheathGroups[cable.guaina].push(cable);
    });

    return (
        <div className="space-y-4">
            {/* Renderizza TUTTI i multipolari */}
            {multipolarCables.map((cable, index) => (
                <div key={`multipolar-${index}`} className="bg-gray-50 rounded-lg p-2" data-cable-index={cable.originalIndex}>
                    <MultipolarCableVisualization
                        cable={cable}
                        hoveredCable={hoveredCable}
                        setHoveredCable={setHoveredCable}
                        isMobile={isMobile}
                        settings={settings}
                    />
                </div>
            ))}

            {/* ✅ Renderizza i cavi MULTIPLI (con priorità) */}
            {multipleGroups.map((group, groupIdx) => {
                // ✅ Verifica se almeno un cavo ha una guaina (per mostrare il badge)
                // Se più cavi hanno guaine diverse, prendiamo la prima trovata
                const cablesWithSheath = group.cables.filter(c => c.guaina);
                const sheathId = cablesWithSheath.length > 0 ? cablesWithSheath[0].guaina : null;
                
                // ✅ Confronto robusto: converte entrambi a Number per il confronto
                const sheath = sheathId ? availableSheaths.find(s => Number(s.id) === Number(sheathId)) : null;
                
                return (
                    <div key={`multi-${groupIdx}`} className="bg-gray-50 rounded-lg p-2" data-cable-indices={group.cables.map(c => c.originalIndex).join(',')}>
                        <MultipleCableVisualization
                            cables={group.cables}
                            siglatura={`${siglatura} (${t('schematic.group')} ${groupIdx + 1})`}
                            hoveredCable={hoveredCable}
                            setHoveredCable={setHoveredCable}
                            isMobile={isMobile}
                            settings={settings}
                            sheath={sheath}
                            availableSheaths={availableSheaths}
                        />
                    </div>
                );
            })}

            {/* ✅ Renderizza i cavi SINGOLI raggruppati per guaina */}
            {Object.entries(sheathGroups).map(([sheathId, cablesToRender]) => {
                const sheath = availableSheaths.find(s => s.id === parseInt(sheathId));
                const sheathLabel = sheath ? (sheath.fullLabel || sheath.label) : `${t('schematic.sheath')} ${sheathId}`;
                
                return (
                    <div key={`sheath-${sheathId}`} className="bg-gray-50 rounded-lg p-2" data-sheath-id={sheathId}>
                        <SheathCableVisualization
                            cables={cablesToRender}
                            sheathLabel={sheathLabel}
                            hoveredCable={hoveredCable}
                            setHoveredCable={setHoveredCable}
                            isMobile={isMobile}
                            settings={settings}
                        />
                    </div>
                );
            })}

            {/* ✅ Renderizza i cavi SINGOLI senza guaina */}
            {singleCablesWithoutSheath.map((cable, index) => (
                <div key={`single-${index}`} className="bg-gray-50 rounded-lg p-2" data-cable-index={cable.originalIndex}>
                    <SingleCableVisualization
                        cable={cable}
                        hoveredCable={hoveredCable}
                        setHoveredCable={setHoveredCable}
                        isMobile={isMobile}
                        settings={settings}
                    />
                </div>
            ))}
        </div>
    );
};

export default HybridCableVisualization;